import { ApplicationHelpType, SortDirections } from '../../../../utilities/Constants';
import {
    ISameCaseMatchResponseModel,
    ISameCaseMatchedCasesSearchModel,
    SameCaseSearchStatusEnum,
    SameCaseSortColumns,
    SameCaseStatusType,
    useGetSameCasesQuery,
    useGetSameCasesQueryKey,
} from './useGetSameCasesQuery';
import { Offcanvas, Stack } from 'react-bootstrap';
import { cloneDeep, isEmpty } from 'lodash';
import { showErrorToast, showSuccessToast } from '../../../../shared/ToastUtils';
import { useEffect, useState } from 'react';

import { CheckmatePagination } from '../../CheckmatePagination';
import { DocumentTitle } from '../../../../components/shared/DocumentTitle';
import { Help } from '../../../../components/shared/Help';
import { Loader } from '../../../../components/shared/Loader';
import { LoadingButton } from '../../../../shared/buttons/LoadingButton';
import { PageWithAffixedHeader } from '../../../../shared/PageWithAffixedHeader';
import { SameCaseAppliedFilters } from '../../../../components/shared/filters/SameCaseAppliedFilters';
import { SameCaseCaseFileModelFilter } from '../../../../components/shared/filters/SameCaseCaseFileModelFilter';
import { SameCaseManageSearchFilters } from '../../../../components/quad/same-cases-admin/manage/SameCaseManageSearchFilters';
import { SaveCaseManageSearchResults } from '../../../../components/quad/same-cases-admin/manage/SaveCaseManageSearchResults';
import UIHelper from '../../../../utilities/UIHelper';
import { useCaseFileModelContext } from '../../../../contexts/useCaseFileModelContext';
import { useProcessAllSameCasesMutation } from './useProcessAllSameCasesMutation';
import { useQueryClient } from 'react-query';

const pageTitle = 'Manage Same Case Groups';
const defaultFilters = {
    pagedModel: {
        pageNumber: 1,
        pageSize: 10,
    },
    sortModel: {
        sortColumn: SameCaseSortColumns.CaseName,
        sortDirection: SortDirections.Asc,
    },
    statuses: [SameCaseSearchStatusEnum.Open],
    showAllNewAndChangesSameCases: true,
};

export function SameCasesManagePage() {
    const [showFilterPane, setShowFilterPane] = useState<boolean>(false);
    const [currentFilters, setCurrentFilters] =
        useState<ISameCaseMatchedCasesSearchModel>(defaultFilters);
    const [newCaseGroupGuidToOpen, setNewCaseGroupGuidToOpen] = useState<string | null>(null);
    const { caseFileModelId, userCaseFileModelsQuery } = useCaseFileModelContext();
    const queryClient = useQueryClient();

    const caseGroupsQuery = useGetSameCasesQuery(
        currentFilters,
        !!userCaseFileModelsQuery?.isFetched && !!caseFileModelId
    );

    const processAllSameCasesMutation = useProcessAllSameCasesMutation();

    const handleSortData = (key: string, order: string) => {
        const sortColumn = parseInt(key) as SameCaseSortColumns;
        const sortDirection =
            order.toUpperCase() === 'DESC' ? SortDirections.Desc : SortDirections.Asc;

        if (
            sortColumn === currentFilters.sortModel.sortColumn &&
            sortDirection === currentFilters.sortModel.sortDirection
        ) {
            return;
        }

        setCurrentFilters((prev) => ({
            ...prev,
            pagedModel: { ...prev.pagedModel, pageNumber: 1 },
            sortModel: {
                sortColumn,
                sortDirection,
            },
        }));
    };

    const handleApplyFilters = (filters: ISameCaseMatchedCasesSearchModel) => {
        const hasDifferentFilters = !isEmpty(
            UIHelper.difference(cloneDeep(filters), cloneDeep(currentFilters))
        );

        if (hasDifferentFilters) {
            setCurrentFilters((prev) => ({
                ...filters,
                pagedModel: { ...prev.pagedModel, pageNumber: 1 },
            }));
        } else {
            setShowFilterPane(false);
            caseGroupsQuery.refetch();
        }
    };

    const handleToggleFilterPane = () => {
        setShowFilterPane((prev) => !prev);
    };

    const handlePageChange = (pageNumber: number) => {
        setCurrentFilters((prev) => ({ ...prev, pagedModel: { ...prev.pagedModel, pageNumber } }));
    };

    const handlePageSizeChange = (pageSize: number) => {
        setCurrentFilters((prev) => ({
            ...prev,
            pagedModel: { ...prev.pagedModel, pageSize, pageNumber: 1 },
        }));
    };

    const handleCaseGroupSaved = (updatedCaseGroup: ISameCaseMatchResponseModel) => {
        const searchModel = currentFilters;

        queryClient.setQueryData([useGetSameCasesQueryKey, { searchModel }], (oldData: any) => {
            if (!oldData) return oldData;

            const prevResults = oldData.results as ISameCaseMatchResponseModel[];
            let newResults: ISameCaseMatchResponseModel[] = cloneDeep(prevResults);

            if (updatedCaseGroup.matchedCases.length === 0) {
                const existingGroup = newResults.find(
                    (r: ISameCaseMatchResponseModel) => r.guid === updatedCaseGroup?.guid
                );

                existingGroup?.matchedCases.forEach((mc) => {
                    mc.sameCaseStatusTypeId = SameCaseStatusType.Pending;
                });
            } else {
                newResults = newResults.map((r: ISameCaseMatchResponseModel) =>
                    r.guid === updatedCaseGroup?.guid ? updatedCaseGroup : r
                );
            }

            return {
                ...oldData,
                results: newResults,
            };
        });

        setNewCaseGroupGuidToOpen(null);
    };

    const handleNewCaseGroupSaved = (
        updatedCaseGroup: ISameCaseMatchResponseModel,
        newCaseGroup: ISameCaseMatchResponseModel
    ) => {
        const searchModel = currentFilters;

        queryClient.setQueryData([useGetSameCasesQueryKey, { searchModel }], (oldData: any) => {
            if (!oldData) return oldData;

            const sameCaseCopy = cloneDeep(oldData);

            const updatedSameCase = sameCaseCopy.results.find(
                (x: any) => x.guid === updatedCaseGroup.guid
            );

            if (!updatedSameCase) return oldData;
            const caseGuidsToRemove = newCaseGroup.matchedCases.map((x) => x.caseGuid);
            const remainingUpdatedCases = updatedSameCase.matchedCases.filter(
                (x: any) => !caseGuidsToRemove.includes(x.caseGuid)
            );

            if (!remainingUpdatedCases) return oldData;

            const updatedSameCaseIndex = sameCaseCopy.results.findIndex(
                (x: any) => x.guid === updatedSameCase.guid
            );

            if (updatedSameCaseIndex === -1) return oldData;

            sameCaseCopy.results[updatedSameCaseIndex].matchedCases = sameCaseCopy.results[
                updatedSameCaseIndex
            ].matchedCases.filter((x: any) => !caseGuidsToRemove.includes(x.caseGuid));

            return {
                ...sameCaseCopy,
                results: [
                    ...sameCaseCopy.results.slice(0, updatedSameCaseIndex),
                    newCaseGroup,
                    ...sameCaseCopy.results.slice(updatedSameCaseIndex),
                ],
            };
        });

        setNewCaseGroupGuidToOpen(newCaseGroup.guid);
    };

    const handleProcessAllSameCasesButtonClick = () => {
        processAllSameCasesMutation
            .mutateAsync()
            .then(() => {
                showSuccessToast(
                    <span>
                        <span>Process All Same Cases Matches Kicked off</span>
                        <p>Please allow 5-10 minutes for processing.</p>
                    </span>
                );
            })
            .catch((error) =>
                showErrorToast(<span>Failed to kick off Process All Same Cases Matches!</span>)
            );
    };

    useEffect(() => {
        if (!caseGroupsQuery.isFetching && caseGroupsQuery.isSuccess) {
            setShowFilterPane(false);
        } else if (!caseGroupsQuery.isFetching && caseGroupsQuery.isError) {
            alert((caseGroupsQuery.error as Error).message);
        }
    }, [
        caseGroupsQuery.isFetching,
        caseGroupsQuery.isSuccess,
        caseGroupsQuery.isError,
        caseGroupsQuery.error,
    ]);

    useEffect(() => {
        setCurrentFilters({ ...defaultFilters, caseFileModelId });
    }, [caseFileModelId]);

    return (
        <>
            <DocumentTitle title={`Quad Admin - ${pageTitle}`} hideZoneName />
            <Offcanvas
                show={showFilterPane}
                onHide={() => setShowFilterPane(false)}
                placement="end"
                scroll
                backdrop
            >
                <Offcanvas.Header
                    closeButton
                    style={{ borderBottom: '1px solid lightgray', height: '77px' }}
                >
                    <Offcanvas.Title>
                        <span style={{ fontSize: 20, fontWeight: 500 }}>{pageTitle} Filters</span>
                    </Offcanvas.Title>
                </Offcanvas.Header>
                <Offcanvas.Body>
                    <SameCaseManageSearchFilters
                        currentFilters={currentFilters}
                        isSearching={caseGroupsQuery.isFetching}
                        onApplyFilters={handleApplyFilters}
                    />
                </Offcanvas.Body>
            </Offcanvas>

            <SameCaseCaseFileModelFilter
                currentFilters={currentFilters}
                setCurrentFilters={setCurrentFilters}
            />

            <PageWithAffixedHeader
                hideHeaderBottomPadding
                headerComponent={
                    <>
                        <Stack
                            direction="horizontal"
                            gap={2}
                            style={{ alignItems: 'start', justifyContent: 'space-between' }}
                        >
                            <h1 style={{ margin: 0 }} className="mb-2">
                                {pageTitle}
                            </h1>
                           {/*  <Stack direction="horizontal">
                                <LoadingButton
                                    buttonCssClass="btn btn-orange"
                                    onClick={handleProcessAllSameCasesButtonClick}
                                    text="Process All"
                                    loadingText="Process..."
                                    loading={processAllSameCasesMutation.isLoading}
                                />
                                <Help
                                    type={ApplicationHelpType.Info}
                                    title="Process All Same Case Matches"
                                    helpText="This process takes 5-10 minutes before it is complete."
                                    additionalStyleClassNames="float-end"
                                />
                            </Stack> */}
                        </Stack>
                        <SameCaseAppliedFilters
                            currentFilters={currentFilters}
                            onApplyFilters={handleApplyFilters}
                            onToggleFilterPane={handleToggleFilterPane}
                        />
                    </>
                }
            >
                {(caseGroupsQuery.isFetching || userCaseFileModelsQuery?.isFetching) && <Loader />}

                <SaveCaseManageSearchResults
                    currentFilters={currentFilters}
                    isFilterPaneOpen={showFilterPane}
                    isLoading={caseGroupsQuery.isLoading}
                    newCaseGroupGuidToOpen={newCaseGroupGuidToOpen}
                    onApplyFilters={handleApplyFilters}
                    onCaseGroupSaved={handleCaseGroupSaved}
                    onNewCaseGroupSaved={handleNewCaseGroupSaved}
                    onSort={handleSortData}
                    onToggleFilterPane={() => setShowFilterPane((prev) => !prev)}
                    results={caseGroupsQuery.data}
                />

                <CheckmatePagination
                    pagedModel={caseGroupsQuery?.data?.pagedModel}
                    pagedModelPrefixText={'Showing'}
                    pagedModelSuffixText={'Same Case Groups'}
                    onPageChange={handlePageChange}
                    onPageSizeChange={handlePageSizeChange}
                />
            </PageWithAffixedHeader>
        </>
    );
}
